import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react'
import styled from 'styled-components'
import Modal from 'components/atoms/Modal'
import { useDispatch, useSelector } from 'react-redux'
import {
  getGuruInsightRoutine,
  clearGuruInsightRoutine
} from 'ducks/guruInsight/actions'
import {
  selectGuruInsight,
  selectIsLoadingGuruInsight,
  selectGuruInsightError
} from 'ducks/guruInsight/selectors'
import { getSelectedBp, selectCurrentBpHasTaskManagement } from 'features/bpDetails/ducks/selectors'
import ActionsAPI from 'services/ActionsAPI'
import { toast } from 'react-toastify'
import {
  CircularProgress,
  Chip,
  Typography,
  Divider,
  Box,
  Skeleton
} from '@mui/material'
import BusinessIcon from '@mui/icons-material/Business'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import TimerOffIcon from '@mui/icons-material/TimerOff'
import EventRepeatIcon from '@mui/icons-material/EventRepeat'
import FindInPageIcon from '@mui/icons-material/FindInPage'
import BuildIcon from '@mui/icons-material/Build'
import VisibilityIcon from '@mui/icons-material/Visibility'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import HistoryIcon from '@mui/icons-material/History'
import SwapHorizIcon from '@mui/icons-material/SwapHoriz'
import EditIcon from '@mui/icons-material/Edit'
import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import { omit, pathOr, propOr } from 'ramda'
import { formatDate } from 'utils/date'
import { getFileUrls, getGuruInsight, rejoinFileVersion, separateFileVersion } from 'services/fileService'
import { replacedBecause } from './versionHistoryText'
import { updateFile } from 'services/BpService'
import PdfFullScreenViewer from 'components/PdfFullScreenViewer'
import ImageViewer from 'components/ImageViewer'
import { Link } from 'react-router-dom'
import { extractActionsFromFileRoutine } from 'features/actions/ducks'
import ConvertToTaskDialog from 'features/actions/components/ConvertToTaskDialog'
import BulkConvertDialog from 'features/actions/components/BulkConvertDialog'
import DocumentTaskLinks from 'features/taskLinks/DocumentTaskLinks'
import LinkTaskDialog from 'features/taskLinks/LinkTaskDialog'
import taskLinksAPI from 'features/taskLinks/taskLinksAPI'
import { errorMessage as linkErrorMessage } from 'features/taskLinks/linkCopy'
import { PRIVATE_PATHS } from 'utils/paths'
import { fetchProgramme } from 'services/ProgrammeService'
import ObligationScopePill from 'features/compliance/ObligationScopePill'

const SEV_COLOUR = { critical: '#b00020', high: '#c2410c', medium: '#d97706', low: '#15803d' }
const SEV_LABEL = { critical: 'Critical', high: 'High', medium: 'Medium', low: 'Low' }

const parseInsightSummary = (summary) => {
  const result = { author: '', date: '', expiry: '', nextInspection: '', keyFindings: [], recommendations: [] }
  if (!summary) return result

  // Pre-process: split inline section headers onto separate lines
  const normalized = summary.replace(/\s+((?:Author|Date|Expiry|Next Inspection|Key Findings|Recommendations\/Actions):)/gi, '\n$1')
  const lines = normalized.split('\n').map(l => l.trim()).filter(Boolean)
  let currentSection = null
  let pendingSingleValue = null // tracks single-value header with no inline value (old format)

  for (const line of lines) {
    const headerMatch = line.match(/^(Author|Date|Expiry|Next Inspection|Key Findings|Recommendations\/Actions):\s*(.*)$/i)
    if (headerMatch) {
      pendingSingleValue = null
      const key = headerMatch[1].toLowerCase()
      const value = headerMatch[2].trim().replace(/\s*(STATED|CALCULATED)\s*$/i, '').trim()
      if (key === 'author') { result.author = value; currentSection = null; if (!value) pendingSingleValue = 'author' }
      else if (key === 'date') { result.date = value; currentSection = null; if (!value) pendingSingleValue = 'date' }
      else if (key === 'expiry') { result.expiry = value; currentSection = null; if (!value) pendingSingleValue = 'expiry' }
      else if (key === 'next inspection') { result.nextInspection = value; currentSection = null; if (!value) pendingSingleValue = 'nextInspection' }
      else if (key === 'key findings') { currentSection = 'findings'; if (value) result.keyFindings.push(value) }
      else if (key.includes('recommend') || key.includes('action')) { currentSection = 'recs'; if (value) result.recommendations.push(value) }
    } else if (pendingSingleValue) {
      // Old format: value is on the line after the header
      result[pendingSingleValue] = line
      pendingSingleValue = null
    } else if (line.startsWith('•')) {
      const text = line.replace(/^•\s*/, '')
      if (currentSection === 'findings') result.keyFindings.push(text)
      else if (currentSection === 'recs') result.recommendations.push(text)
    }
  }
  return result
}

const TAG_LAYER_LABELS = {
  product_guarantee: 'Products warranted',
  status: 'Status',
  drawing_type: 'Drawing type',
  discipline: 'Discipline',
  system: 'System',
  element: 'Element',
  space: 'Space',
  external: 'External',
  context: 'Context',
  specialist: 'Specialist',
  direction: 'Direction',
  other: 'Tags',
}


const GuruInsightModal = ({ open, onClose, onUpdate, file, bpId: propBpId, buildingPassportName: propBpName, portfolioId = null }) => {
  const dispatch = useDispatch()
  const bp = useSelector(getSelectedBp)
  const currentBpHasTaskManagement = useSelector(selectCurrentBpHasTaskManagement)
  const insight = useSelector(selectGuruInsight)
  const isLoading = useSelector(selectIsLoadingGuruInsight)
  const insightError = useSelector(selectGuruInsightError)
  // activeFile lets the user navigate within the renewal chain / flags without closing the modal.
  // Defaults to the file prop and resets whenever the prop changes.
  const [activeFile, setActiveFile] = useState(file)
  useEffect(() => { setActiveFile(file) }, [file?.id])
  const activeFileIdRef = useRef(null)
  activeFileIdRef.current = activeFile?.id ?? null

  const thumbnailFromStore = useSelector(state => activeFile?.id ? state.files.thumbnailsById[activeFile.id] : undefined)
  const [previewOpen, setPreviewOpen] = useState(false)
  const [previewFile, setPreviewFile] = useState(null)
  const chainSectionRef = useRef(null)
  const [loadingPreview, setLoadingPreview] = useState(false)

  const effectiveBpId = portfolioId ? null : (propBpId || bp?.id)
  const effectiveBpName = propBpName || activeFile?.buildingPassportName || bp?.name

  const handlePreview = useCallback(async () => {
    const target = activeFile
    if (!effectiveBpId || !target?.id) return
    setLoadingPreview(true)
    try {
      const res = await getFileUrls(effectiveBpId, target.id)
      const urls = res.data?.data || res.data
      const fileUrl = urls.url || ''
      const fileName = target.displayName || ''

      if (/\.pdf/i.test(fileUrl) || /\.pdf/i.test(fileName)) {
        setPreviewFile({ ...target, url: fileUrl, originalUrl: urls.originalUrl })
        setPreviewOpen(true)
      } else if (/\.(jpg|jpeg|png|gif|svg|webp)$/i.test(fileUrl) || /\.(jpg|jpeg|png|gif|svg|webp)$/i.test(fileName)) {
        setPreviewFile({ ...target, url: fileUrl, originalUrl: urls.originalUrl })
        setPreviewOpen(true)
      } else {
        window.open(urls.originalUrl || fileUrl, '_blank')
      }
    } catch (e) {
      window.open(`/bp-list/${effectiveBpId}`, '_blank')
    } finally {
      setLoadingPreview(false)
    }
  }, [effectiveBpId, activeFile])

  // Navigate to a different file in the chain/flag without closing the modal
  const handleChainNavigate = useCallback((targetFileId, targetDisplayName) => {
    if (!targetFileId || targetFileId === activeFile?.id) return
    setActiveFile({ id: targetFileId, displayName: targetDisplayName || 'Document' })
  }, [activeFile?.id])

  // Format the insight data by removing the fromCache field
  const formattedInsight = useMemo(() => {
    if (!insight) return null
    return omit(['fromCache'], insight)
  }, [insight])

  const remedialsBpId = portfolioId ? null : (effectiveBpId || formattedInsight?.file?.buildingPassportId || null)

  // When the user navigates via the chain or a flag, activeFile only has { id, displayName }.
  // Merge in the full metadata from the insight response (backend returns `file` object) so
  // File Type, Upload Date, Uploader etc. all populate for the navigated-to document.
  const displayFile = useMemo(() => {
    if (!activeFile) return file
    const insightFile = formattedInsight?.file
    // Only merge insight data if it's for the same file we're currently viewing
    if (insightFile && insightFile.id === activeFile.id) {
      return { ...activeFile, ...insightFile }
    }
    return activeFile
  }, [activeFile, formattedInsight, file])

  const effectiveThumbnail = thumbnailFromStore || displayFile?.thumbnailUrl

  const [scopeObligations, setScopeObligations] = useState([])
  const [canDecideCompliance, setCanDecideCompliance] = useState(false)
  const displayFileTypeId = displayFile?.fileTypeId || displayFile?.fileType?.id || null

  const loadScopeObligations = useCallback(() => {
    if (!open || !effectiveBpId || !displayFileTypeId) {
      setScopeObligations([])
      return
    }
    fetchProgramme(effectiveBpId)
      .then(res => {
        const data = res?.data?.data
        setCanDecideCompliance(!!data?.canDecideCompliance)
        const matches = []
        ;(data?.domains || []).forEach(d => {
          ;(d.obligations || []).forEach(o => {
            if (o.kind !== 'file_type' || o.isSkipped) return
            const accepted = (o.acceptedFileTypes || []).some(t => (t?.id || t) === displayFileTypeId)
            if (o.fileTypeId === displayFileTypeId || accepted) {
              matches.push({ id: o.id, name: o.name, scope: o.scope, unitCoverage: o.unitCoverage })
            }
          })
        })
        setScopeObligations(matches)
      })
      .catch(() => setScopeObligations([]))
  }, [open, effectiveBpId, displayFileTypeId])

  useEffect(() => { loadScopeObligations() }, [loadScopeObligations])

  const [editingField, setEditingField] = useState(null)
  const [editValue, setEditValue] = useState('')
  const [saving, setSaving] = useState(false)
  const [convertTarget, setConvertTarget] = useState(null)
  const [convertedTasks, setConvertedTasks] = useState(() => new Map())
  const markConverted = (id, task) => setConvertedTasks(prev => new Map(prev).set(id, task || prev.get(id) || null))
  const [linkTarget, setLinkTarget] = useState(null)
  const [linksRefresh, setLinksRefresh] = useState(0)

  const linkExistingTask = async (rem, task, relation) => {
    try {
      await taskLinksAPI.link(rem.id, task.id, relation)
      toast.success(relation === 'handles' ? 'Task linked. This remedial will close when the task is completed.' : 'Task linked to the remedial for context.')
      if (relation === 'handles') markConverted(rem.id, { id: task.id, title: task.title, status: task.status })
      setLinksRefresh(n => n + 1)
      onUpdate?.()
    } catch (err) {
      toast.error(linkErrorMessage(err))
    }
  }
  const [approvedIds, setApprovedIds] = useState(() => new Set())
  const [dismissedIds, setDismissedIds] = useState(() => new Set())
  const [approving, setApproving] = useState(false)
  const [dismissing, setDismissing] = useState(false)
  const [bulkConvertIds, setBulkConvertIds] = useState(null)
  const [showDealtWith, setShowDealtWith] = useState(false)

  const [versionBusy, setVersionBusy] = useState(false)
  const changeVersionHistory = async separate => {
    const fileId = activeFile?.id
    if (!fileId || versionBusy) return
    setVersionBusy(true)
    try {
      await (separate ? separateFileVersion(fileId) : rejoinFileVersion(fileId))
      toast.success(separate ? 'Taken out of the version history. It now stays current on its own.' : 'Put back in the version history.')
      refreshInsight()
      onUpdate?.()
    } catch {
      toast.error('Could not change the version history. Please try again.')
    } finally {
      setVersionBusy(false)
    }
  }

  const refreshInsight = () => {
    const fileId = activeFile?.id
    if (!fileId || !remedialsBpId) return
    getGuruInsight({ bpId: remedialsBpId, fileId })
      .then(res => {
        if (activeFileIdRef.current === fileId) dispatch(getGuruInsightRoutine.success(res.data))
      })
      .catch(() => {})
  }

  const approveAll = async ids => {
    if (approving || !ids.length) return
    setApproving(true)
    try {
      const res = await ActionsAPI.bulkApprove({ ids })
      const done = res?.data?.data?.approvedIds || []
      setApprovedIds(prev => { const next = new Set(prev); done.forEach(id => next.add(id)); return next })
      onUpdate?.()
      toast.success(`${done.length} remedial${done.length === 1 ? '' : 's'} approved and now active. Create tasks for them whenever you're ready.`)
    } catch {
      toast.error('Could not approve these remedials. Please try again.')
    } finally {
      setApproving(false)
    }
  }

  const undoDismissMany = async ids => {
    setDismissing(true)
    const results = await Promise.allSettled(ids.map(id => ActionsAPI.undismiss(id)))
    const restored = ids.filter((id, i) => results[i].status === 'fulfilled')
    setDismissedIds(prev => { const next = new Set(prev); restored.forEach(id => next.delete(id)); return next })
    setDismissing(false)
    onUpdate?.()
    if (restored.length === ids.length) toast.success('Put back for review.')
    else toast.error(`Only ${restored.length} of ${ids.length} could be put back. Please try again.`)
  }

  const dismissAll = async ids => {
    if (dismissing || !ids.length) return
    setDismissing(true)
    try {
      const res = await ActionsAPI.bulkDismiss({ ids, reason: 'Not a real issue' })
      const done = res?.data?.data?.dismissedIds || []
      setDismissedIds(prev => { const next = new Set(prev); done.forEach(id => next.add(id)); return next })
      onUpdate?.()
      toast.success(({ closeToast }) => (
        <ToastLine>
          <span>{done.length} removed as not real issues.</span>
          <ToastUndo type='button' onClick={() => { closeToast?.(); undoDismissMany(done) }}>Undo</ToastUndo>
        </ToastLine>
      ), { autoClose: 10000 })
    } catch {
      toast.error('These AI finds could not be removed. Please try again.')
    } finally {
      setDismissing(false)
    }
  }

  const approveRemedial = async (rem) => {
    if (approving) return
    setApproving(true)
    try {
      await ActionsAPI.approve(rem.id)
      setApprovedIds(prev => new Set(prev).add(rem.id))
      onUpdate?.()
      toast.success("Remedial approved and now active. Create a task for it whenever you're ready.")
    } catch {
      toast.error('Could not approve this remedial. Please try again.')
    } finally {
      setApproving(false)
    }
  }

  const dismissRemedial = async (rem) => {
    if (dismissing) return
    setDismissing(true)
    try {
      await ActionsAPI.dismiss(rem.id, {})
      setDismissedIds(prev => new Set(prev).add(rem.id))
      onUpdate?.()
      toast.success('Removed as not a real issue. You can undo this.')
    } catch {
      toast.error('This AI find could not be removed. Please try again.')
    } finally {
      setDismissing(false)
    }
  }

  const undismissRemedial = async (rem) => {
    if (dismissing) return
    setDismissing(true)
    try {
      await ActionsAPI.undismiss(rem.id)
      setDismissedIds(prev => {
        const next = new Set(prev)
        next.delete(rem.id)
        return next
      })
      onUpdate?.()
      toast.success('Remedial restored, back for review.')
    } catch {
      toast.error('Could not undo that. Please try again.')
    } finally {
      setDismissing(false)
    }
  }

  const handleStartEdit = useCallback((field, currentValue) => {
    setEditingField(field)
    setEditValue(currentValue || '')
  }, [])

  const handleCancelEdit = useCallback(() => {
    setEditingField(null)
    setEditValue('')
  }, [])

  const handleSaveDate = useCallback(async () => {
    const fileId = activeFile?.id || displayFile?.id
    const versionId = formattedInsight?.fileVersionId
    if (!fileId || !versionId) return

    setSaving(true)
    try {
      const body = editingField === 'expiry'
        ? { expirationDate: editValue || null }
        : { nextInspectionDate: editValue || null }
      await updateFile({ id: fileId, body, fileVersionId: versionId })
      setEditingField(null)
      setEditValue('')
      dispatch(getGuruInsightRoutine({ bpId: effectiveBpId, fileId }))
      onUpdate?.()
    } catch (e) {
      console.error('Failed to save date:', e)
    } finally {
      setSaving(false)
    }
  }, [activeFile, displayFile, formattedInsight, editingField, editValue, effectiveBpId, dispatch, onUpdate])

  useEffect(() => {
    if (open && activeFile && (effectiveBpId || portfolioId)) {
      dispatch(getGuruInsightRoutine({ bpId: effectiveBpId, portfolioId, fileId: activeFile.id }))
    }

    return () => {
      dispatch(clearGuruInsightRoutine())
    }
  }, [open, activeFile?.id, propBpId, portfolioId, bp, dispatch])

  return (
    <>
    <Modal
      open={open}
      onClose={onClose}
      title='Guru Insight'
      maxWidth='md'
      fullWidth
    >
      <Container>
        {isLoading ? (
          <LoadingContainer>
            <LoadingContent>
              <LoadingHeader>
                <SkeletonThumbnail />
                <LoadingInfo>
                  <Typography variant='h6'>
                    <Skeleton width='70%' height={32} />
                  </Typography>
                  <SkeletonDetails>
                    <SkeletonDetailItem />
                    <SkeletonDetailItem />
                    <SkeletonDetailItem />
                    <SkeletonDetailItem />
                  </SkeletonDetails>
                </LoadingInfo>
              </LoadingHeader>
              <Divider sx={{ margin: '16px 0' }} />
              <LoadingMessage>
                <CircularProgress size={24} sx={{ marginRight: '12px' }} />
                Analysing document and generating insights...
              </LoadingMessage>
              <SkeletonInsight>
                <Skeleton
                  width='40%'
                  height={32}
                  style={{ marginBottom: '16px' }}
                />
                <Skeleton width='100%' height={20} />
                <Skeleton width='100%' height={20} />
                <Skeleton width='90%' height={20} />
                <Skeleton width='95%' height={20} />
                <Skeleton width='100%' height={20} />
                <Skeleton width='80%' height={20} />
              </SkeletonInsight>
            </LoadingContent>
          </LoadingContainer>
        ) : formattedInsight ? (
          <>
            <HeaderSection data-tour-id='guruInsight.header'>
              <ThumbnailContainer>
                {effectiveThumbnail ? (
                  <Thumbnail src={effectiveThumbnail} alt={displayFile?.displayName} />
                ) : (
                  <NoThumbnail>No thumbnail available</NoThumbnail>
                )}
              </ThumbnailContainer>
              <FileInfoContainer>
                <Typography variant='h6' component='h2' gutterBottom style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                  <FileName title={displayFile?.displayName}>{displayFile?.displayName}</FileName>
                  {formattedInsight.isSuperseded && <DocStatusChip $type="superseded" style={{ cursor: 'pointer' }} onClick={() => chainSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}>Superseded</DocStatusChip>}
                  {!formattedInsight.isSuperseded && formattedInsight.renewalChain?.length > 1 && <DocStatusChip $type="current">Current</DocStatusChip>}
                </Typography>
                {activeFile?.id !== file?.id && (
                  <BackToOriginalLink onClick={() => setActiveFile(file)}>
                    ← Back to {file?.displayName}
                  </BackToOriginalLink>
                )}
                <FileActions>
                  {effectiveBpName && (
                    <BpNameChip>{effectiveBpName}</BpNameChip>
                  )}
                  <PreviewButton onClick={handlePreview} disabled={loadingPreview}>
                    <VisibilityIcon style={{ fontSize: 16 }} />
                    {loadingPreview ? 'Loading...' : 'Preview document'}
                  </PreviewButton>
                </FileActions>

                <FileDetailsSection>
                  <FileDetailItem>
                    <DetailLabel>File Type:</DetailLabel>
                    <DetailValue>
                      {typeof displayFile?.fileType === 'string' ? displayFile.fileType : pathOr('Unknown', ['fileType', 'name'], displayFile)}
                    </DetailValue>
                  </FileDetailItem>

                  {scopeObligations.length > 0 && (
                    <FileDetailItem>
                      <DetailLabel>Compliance requirement:</DetailLabel>
                      <DetailValue style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {scopeObligations.map(o => (
                          <span key={o.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                            <ObligationScopePill
                              bpId={effectiveBpId}
                              obligation={o}
                              canEdit={canDecideCompliance}
                              onChanged={loadScopeObligations}
                            />
                            <span style={{ fontSize: 12, color: '#4d4d56' }}>{o.name}</span>
                          </span>
                        ))}
                      </DetailValue>
                    </FileDetailItem>
                  )}

                  {displayFile?.createdAt && (
                    <FileDetailItem>
                      <DetailLabel>Upload Date:</DetailLabel>
                      <DetailValue>
                        {formatDate(displayFile.createdAt)}
                      </DetailValue>
                    </FileDetailItem>
                  )}

                  {displayFile?.uploader && (
                    <FileDetailItem>
                      <DetailLabel>Uploaded By:</DetailLabel>
                      <DetailValue>
                        {propOr('', 'firstName', displayFile.uploader)}{' '}
                        {propOr('', 'lastName', displayFile.uploader)}
                      </DetailValue>
                    </FileDetailItem>
                  )}

                  {displayFile?.contentType && (
                    <FileDetailItem>
                      <DetailLabel>Content Type:</DetailLabel>
                      <DetailValue>{displayFile.contentType}</DetailValue>
                    </FileDetailItem>
                  )}

                  {displayFile?.size && (
                    <FileDetailItem>
                      <DetailLabel>File Size:</DetailLabel>
                      <DetailValue>
                        {(displayFile.size / 1024).toFixed(2)} KB
                      </DetailValue>
                    </FileDetailItem>
                  )}

                  {displayFile?.versions && displayFile.versions.length > 0 && (
                    <FileDetailItem>
                      <DetailLabel>Version:</DetailLabel>
                      <DetailValue>{displayFile.versions.length}</DetailValue>
                    </FileDetailItem>
                  )}

                  {formattedInsight.generatedAt && (
                    <FileDetailItem>
                      <DetailLabel>Insight Generated:</DetailLabel>
                      <DetailValue>
                        {new Date(
                          formattedInsight.generatedAt
                        ).toLocaleDateString()}
                      </DetailValue>
                    </FileDetailItem>
                  )}
                </FileDetailsSection>

                {Array.isArray(displayFile?.tags) && displayFile.tags.length > 0 && (
                  <FileTagsSection>
                    {Object.entries(
                      displayFile.tags.reduce((acc, t) => {
                        const key = t.layer || 'other'
                        ;(acc[key] = acc[key] || []).push(t)
                        return acc
                      }, {})
                    ).map(([layer, tags]) => (
                      <TagGroup key={layer}>
                        <TagGroupLabel>{TAG_LAYER_LABELS[layer] || layer.replace(/_/g, ' ')}</TagGroupLabel>
                        <TagChipRow>
                          {tags.map(t => (
                            <TagChip key={t.name} title={t.isSystem ? 'Auto-tagged by AI' : undefined}>{t.name}</TagChip>
                          ))}
                        </TagChipRow>
                      </TagGroup>
                    ))}
                  </FileTagsSection>
                )}
              </FileInfoContainer>
            </HeaderSection>

            <Divider sx={{ margin: '16px 0' }} />

            <InsightContainer>
              {formattedInsight.summary && (() => {
                const parsed = parseInsightSummary(formattedInsight.summary)
                return (
                  <>
                    <MetadataGrid>
                      <MetadataCard>
                        <MetadataIcon $color='#6366f1'><BusinessIcon fontSize='small' /></MetadataIcon>
                        <MetadataContent>
                          <MetadataLabel>Author</MetadataLabel>
                          <MetadataValue>{parsed.author || 'Not specified'}</MetadataValue>
                        </MetadataContent>
                      </MetadataCard>
                      <MetadataCard>
                        <MetadataIcon $color='#0891b2'><CalendarTodayIcon fontSize='small' /></MetadataIcon>
                        <MetadataContent>
                          <MetadataLabel>Document Date</MetadataLabel>
                          <MetadataValue>{parsed.date || 'Not specified'}</MetadataValue>
                        </MetadataContent>
                      </MetadataCard>
                      <MetadataCard>
                        <MetadataIcon $color={parsed.expiry && parsed.expiry !== 'Not applicable' ? '#dc2626' : '#9ca3af'}>
                          <TimerOffIcon fontSize='small' />
                        </MetadataIcon>
                        <MetadataContent>
                          <MetadataLabel>Expiry</MetadataLabel>
                          {editingField === 'expiry' ? (
                            <InlineEditRow>
                              <InlineDateInput type="date" value={editValue} onChange={e => setEditValue(e.target.value)} autoFocus />
                              <InlineEditBtn onClick={handleSaveDate} disabled={saving} title="Save">
                                <CheckIcon style={{ fontSize: 14 }} />
                              </InlineEditBtn>
                              <InlineEditBtn onClick={handleCancelEdit} $cancel title="Cancel">
                                <CloseIcon style={{ fontSize: 14 }} />
                              </InlineEditBtn>
                            </InlineEditRow>
                          ) : (
                            <MetadataValue>
                              {formattedInsight.expirationDate ? formatDate(formattedInsight.expirationDate) : (parsed.expiry || 'Not applicable')}
                              {formattedInsight.expiryDateSource && (
                                <DateSourceTag $source={formattedInsight.expiryDateSource}>
                                  {formattedInsight.expiryDateSource === 'user' ? 'Manual' : formattedInsight.expiryDateSource === 'ai_extracted' ? 'AI' : formattedInsight.expiryDateSource === 'calculated' ? 'Estimated' : ''}
                                </DateSourceTag>
                              )}
                              {formattedInsight.fileVersionId && (
                                <EditButton onClick={(e) => { e.stopPropagation(); handleStartEdit('expiry', formattedInsight.expirationDate || '') }} title="Edit expiry date">
                                  <EditIcon style={{ fontSize: 13 }} />
                                </EditButton>
                              )}
                            </MetadataValue>
                          )}
                        </MetadataContent>
                      </MetadataCard>
                      <MetadataCard>
                        <MetadataIcon $color={parsed.nextInspection && parsed.nextInspection !== 'Not applicable' ? '#f59e0b' : '#9ca3af'}>
                          <EventRepeatIcon fontSize='small' />
                        </MetadataIcon>
                        <MetadataContent>
                          <MetadataLabel>Next Inspection</MetadataLabel>
                          {editingField === 'inspection' ? (
                            <InlineEditRow>
                              <InlineDateInput type="date" value={editValue} onChange={e => setEditValue(e.target.value)} autoFocus />
                              <InlineEditBtn onClick={handleSaveDate} disabled={saving} title="Save">
                                <CheckIcon style={{ fontSize: 14 }} />
                              </InlineEditBtn>
                              <InlineEditBtn onClick={handleCancelEdit} $cancel title="Cancel">
                                <CloseIcon style={{ fontSize: 14 }} />
                              </InlineEditBtn>
                            </InlineEditRow>
                          ) : (
                            <MetadataValue>
                              {formattedInsight.nextInspectionDate ? formatDate(formattedInsight.nextInspectionDate) : (parsed.nextInspection || 'Not applicable')}
                              {formattedInsight.fileVersionId && (
                                <EditButton onClick={(e) => { e.stopPropagation(); handleStartEdit('inspection', formattedInsight.nextInspectionDate || '') }} title="Edit next inspection date">
                                  <EditIcon style={{ fontSize: 13 }} />
                                </EditButton>
                              )}
                            </MetadataValue>
                          )}
                        </MetadataContent>
                      </MetadataCard>
                      {formattedInsight.renewalPeriod && (
                        <MetadataCard>
                          <MetadataIcon $color='#7c3aed'>
                            <HistoryIcon fontSize='small' />
                          </MetadataIcon>
                          <MetadataContent>
                            <MetadataLabel>Renewal Period</MetadataLabel>
                            <MetadataValue>{formattedInsight.renewalPeriod}</MetadataValue>
                          </MetadataContent>
                        </MetadataCard>
                      )}
                    </MetadataGrid>

                    {(formattedInsight.scope || formattedInsight.scopes || formattedInsight.buildingAsset || formattedInsight.chainArea) && (
                      <ScopeInfo>
                        {(formattedInsight.scopes || [formattedInsight.scope]).includes('building') && 'Applies per building. '}
                        {(formattedInsight.scopes || [formattedInsight.scope]).includes('unit') && 'Applies per unit/flat. '}
                        {(formattedInsight.scopes || [formattedInsight.scope]).includes('asset') && (
                          formattedInsight.buildingAsset
                            ? `Linked to: ${formattedInsight.buildingAsset.name} (${formattedInsight.buildingAsset.assetType}). `
                            : 'Applies per asset (e.g. lift, generator). '
                        )}
                        {formattedInsight.chainArea && `Area: ${formattedInsight.chainArea}.`}
                      </ScopeInfo>
                    )}

                    {!formattedInsight.renewalPeriod && (formattedInsight.expirationDate || formattedInsight.nextInspectionDate) && (
                      <CalendarNote $type="info">
                        This document type does not have a renewal period set, so it won't appear in the renewal calendar or upcoming renewals list.
                      </CalendarNote>
                    )}

                    {parsed.keyFindings.length > 0 && (
                      <FindingsSection data-tour-id='guruInsight.findings'>
                        <FindingsHeader>
                          <FindInPageIcon fontSize='small' style={{ color: '#059669' }} />
                          Key Findings
                        </FindingsHeader>
                        {parsed.keyFindings.map((finding, i) => (
                          <BulletPoint key={i}>{finding}</BulletPoint>
                        ))}
                      </FindingsSection>
                    )}

                    {(() => {
                      const remedials = (formattedInsight?.remedials || []).map(r => {
                        if (!convertedTasks.has(r.id) || r.status === 'resolved') return r
                        const known = convertedTasks.get(r.id)
                        const tasks = r.tasks?.length ? r.tasks : (known ? [known] : [])
                        return { ...r, status: 'converted', tasks }
                      })
                      if (remedials.length > 0) {
                        const toTaskTarget = rem => ({ ...rem, buildingPassport: { ...(rem.buildingPassport || {}), name: effectiveBpName, hasTaskManagement: currentBpHasTaskManagement, isOwner: bp?.isOwner } })
                        const isDone = r => r.status === 'converted' || r.status === 'resolved'
                        const waiting = remedials.filter(r => !isDone(r) && (dismissedIds.has(r.id) || (r.status === 'pending_review' && !approvedIds.has(r.id))))
                        const needsTask = remedials.filter(r => !isDone(r) && !waiting.includes(r))
                        const dealtWith = remedials.filter(isDone)
                        const pendingIds = remedialsBpId ? waiting.filter(r => !dismissedIds.has(r.id)).map(r => r.id) : []
                        const busyNow = approving || dismissing
                        const title = rem => (
                          <RemedialInfo>
                            {rem.severity && (
                              <PriorityTag $color={SEV_COLOUR[rem.severity] || '#6b7280'}>
                                {(SEV_LABEL[rem.severity] || rem.severity).toUpperCase()}
                              </PriorityTag>
                            )}
                            <RemedialText $dismissed={dismissedIds.has(rem.id)}>{rem.title}</RemedialText>
                          </RemedialInfo>
                        )
                        return (
                          <FindingsSection>
                            <FindingsHeader>
                              <BuildIcon fontSize='small' style={{ color: '#d97706' }} />
                              Recommended remedials ({remedials.length})
                            </FindingsHeader>

                            {waiting.length > 0 && (
                              <RemedialGroup aria-label='Waiting for you to check'>
                                <GroupHead>
                                  <GroupTitle>Waiting for you to check ({pendingIds.length})</GroupTitle>
                                  <GroupHint>Found by our AI. Approve the real problems and remove the rest.</GroupHint>
                                  {remedialsBpId && pendingIds.length > 1 && (
                                    <GroupActions>
                                      <SolidBtn type='button' disabled={busyNow} onClick={() => setBulkConvertIds(pendingIds)}
                                        title='Approve all of these and create a task for each one, in one step'>
                                        Approve all and create tasks
                                      </SolidBtn>
                                      <OutlineBtn type='button' disabled={busyNow} onClick={() => approveAll(pendingIds)}
                                        title='Approve all of these. You can create tasks for them afterwards.'>
                                        Approve all
                                      </OutlineBtn>
                                      <TextBtn type='button' disabled={busyNow} onClick={() => dismissAll(pendingIds)}
                                        title='None of these are real problems. You can undo this.'>
                                        None are real issues
                                      </TextBtn>
                                    </GroupActions>
                                  )}
                                </GroupHead>
                                {waiting.map(rem => (
                                  <RemedialRow key={rem.id} $dismissed={dismissedIds.has(rem.id)}>
                                    {title(rem)}
                                    {remedialsBpId && (dismissedIds.has(rem.id)
                                      ? (
                                        <RemedialActions>
                                          <DismissedTag>Not a real issue</DismissedTag>
                                          <TextBtn type='button' disabled={dismissing} onClick={() => undismissRemedial(rem)} title='Put this back for review'>Undo</TextBtn>
                                        </RemedialActions>
                                      )
                                      : (
                                        <RemedialActions>
                                          <OutlineBtn type='button' disabled={busyNow} onClick={() => approveRemedial(rem)}
                                            title='Approve: confirms this is a real problem. You can then create a task for it.'>
                                            Approve
                                          </OutlineBtn>
                                          <TextBtn type='button' disabled={busyNow} onClick={() => dismissRemedial(rem)}
                                            title='Not a real issue: removes this AI find. You can undo it.'>
                                            Not a real issue
                                          </TextBtn>
                                        </RemedialActions>
                                      ))}
                                  </RemedialRow>
                                ))}
                              </RemedialGroup>
                            )}

                            {needsTask.length > 0 && (
                              <RemedialGroup aria-label='Approved, needs a task'>
                                <GroupHead>
                                  <GroupTitle>Approved, needs a task ({needsTask.length})</GroupTitle>
                                  <GroupHint>Plan the work to fix each one, or link a task you already have.</GroupHint>
                                </GroupHead>
                                {needsTask.map(rem => (
                                  <RemedialRow key={rem.id}>
                                    {title(rem)}
                                    {remedialsBpId && (
                                      <RemedialActions>
                                        <OutlineBtn type='button' onClick={() => setConvertTarget(toTaskTarget(rem))} title='Create a task to fix this'>
                                          Create task
                                        </OutlineBtn>
                                        <TextBtn type='button' onClick={() => setLinkTarget(rem)} title='Link a task that already exists and deals with this.'>
                                          Link a task
                                        </TextBtn>
                                      </RemedialActions>
                                    )}
                                  </RemedialRow>
                                ))}
                              </RemedialGroup>
                            )}

                            {dealtWith.length > 0 && (
                              <RemedialGroup aria-label='Already being dealt with'>
                                <DoneToggle type='button' aria-expanded={showDealtWith} onClick={() => setShowDealtWith(v => !v)}>
                                  {showDealtWith ? 'Hide' : 'Show'} {dealtWith.length} already being dealt with
                                </DoneToggle>
                                {showDealtWith && dealtWith.map(rem => {
                                  const firstTask = (rem.tasks || [])[0]
                                  const moreTasks = Math.max(0, (rem.tasks || []).length - 1)
                                  return (
                                    <RemedialRow key={rem.id} $muted>
                                      {title(rem)}
                                      <RemedialActions>
                                        <StageTag
                                          $stage={rem.status}
                                          title={rem.status === 'resolved'
                                            ? 'This remedial is closed: its task has been completed.'
                                            : 'A task is planned for this remedial. It closes by itself when the task is completed.'}
                                        >
                                          {rem.status === 'resolved' ? '✓ Resolved' : 'Task created'}
                                        </StageTag>
                                        {firstTask?.id && (
                                          <ViewTaskLink to={`/tasks/${firstTask.id}`} title={firstTask.title || undefined}>
                                            View task{moreTasks ? ` (+${moreTasks})` : ''}
                                          </ViewTaskLink>
                                        )}
                                      </RemedialActions>
                                    </RemedialRow>
                                  )
                                })}
                              </RemedialGroup>
                            )}
                          </FindingsSection>
                        )
                      }
                      return null
                    })()}

                    {activeFile?.id && remedialsBpId && (
                      <DocumentTaskLinks
                        bpId={remedialsBpId}
                        fileId={activeFile.id}
                        refreshKey={linksRefresh}
                        onChanged={rel => {
                          if (rel?.handled) markConverted(rel.handled)
                          if (rel?.unlinked) {
                            setConvertedTasks(prev => {
                              if (!prev.has(rel.unlinked)) return prev
                              const next = new Map(prev)
                              next.delete(rel.unlinked)
                              return next
                            })
                            const fileId = activeFile.id
                            getGuruInsight({ bpId: remedialsBpId, fileId })
                              .then(res => {
                                if (activeFileIdRef.current === fileId) dispatch(getGuruInsightRoutine.success(res.data))
                              })
                              .catch(() => {})
                          }
                          onUpdate?.()
                        }}
                      />
                    )}
                  </>
                )
              })()}
            </InsightContainer>

            {activeFile?.id && remedialsBpId && (
              <RemedialsSegue>
                <SegueText>
                  Approved remedials and their tasks are tracked in <strong>Remedials</strong>, together with everything else for {effectiveBpName || 'this building'}.
                </SegueText>
                <SegueButtons>
                  <SegueLink to={`${PRIVATE_PATHS.actions}?bp=${remedialsBpId}`}>Open Remedials for this building →</SegueLink>
                  {formattedInsight?.summary ? (
                    <ReExtractButton
                      type='button'
                      title="Asks Guru to read this document again and look for problems. It only adds new remedials you don't already have, and it never duplicates or removes existing ones."
                      onClick={() => dispatch(extractActionsFromFileRoutine(activeFile.id))}
                    >
                      Look again for problems
                    </ReExtractButton>
                  ) : (
                    <SegueHint>Guru couldn’t find any text to read in this document, so it can’t look for problems in it.</SegueHint>
                  )}
                </SegueButtons>
              </RemedialsSegue>
            )}

            {formattedInsight.contractors?.length > 0 && (
              <ContractorsSection>
                <FindingsHeader>
                  <BusinessIcon fontSize='small' style={{ color: '#0891b2' }} />
                  Contractors identified ({formattedInsight.contractors.length})
                </FindingsHeader>
                <ChainDescription>
                  Detected in this document. Review and manage them in Contractors.
                </ChainDescription>
                <ContractorsList>
                  {formattedInsight.contractors.map(c => (
                    <ContractorChip key={c.id} $inactive={!c.isActive}>
                      <strong>{c.company || c.name}</strong>
                      {c.trade && <span> · {c.trade}</span>}
                      {!c.isActive && <ContractorMuted>inactive</ContractorMuted>}
                    </ContractorChip>
                  ))}
                </ContractorsList>
                <ActionsLink to={PRIVATE_PATHS.contractors}>
                  Open Contractors →
                </ActionsLink>
              </ContractorsSection>
            )}

            {formattedInsight.isSuperseded && formattedInsight.supersededBy && (
              <SupersededBanner>
                <SwapHorizIcon style={{ fontSize: 16 }} />
                <span>This document has been <strong
                  style={{ cursor: 'pointer', textDecoration: 'underline' }}
                  onClick={() => chainSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                >superseded</strong> by <strong
                  style={{ cursor: 'pointer', textDecoration: 'underline' }}
                  onClick={() => handleChainNavigate(formattedInsight.supersededBy.fileId, formattedInsight.supersededBy.displayName)}
                >{formattedInsight.supersededBy.displayName}</strong>. Superseded documents are excluded from the calendar and renewal tracking. Only the current version is shown.</span>
              </SupersededBanner>
            )}

            {formattedInsight.versionSeparated && (
              <ReplacesBanner>
                <HistoryIcon style={{ fontSize: 16 }} />
                <span>This document was taken out of its version history, so it stays current on its own.</span>
                {bp?.canEdit && (
                  <TextBtn type='button' disabled={versionBusy} onClick={() => changeVersionHistory(false)}>
                    Put it back in the history
                  </TextBtn>
                )}
              </ReplacesBanner>
            )}

            {!formattedInsight.isSuperseded && formattedInsight.supersedes?.length > 0 && (
              <ReplacesBanner>
                <HistoryIcon style={{ fontSize: 16 }} />
                <span>This is the <strong>current version</strong>. It replaces: {formattedInsight.supersedes.map(f => f.displayName).join(', ')}</span>
              </ReplacesBanner>
            )}

            {(() => {
              const usingRenewal = (formattedInsight.renewalChain?.length || 0) > 1
              const historyItems = [...(usingRenewal
                ? formattedInsight.renewalChain
                : (formattedInsight.supersessionChain || []))].reverse()
              if (historyItems.length < 2) return null
              return (
              <RenewalChainSection ref={chainSectionRef}>
                <FindingsHeader>
                  <HistoryIcon fontSize='small' style={{ color: '#7c3aed' }} />
                  Document History
                  {usingRenewal && formattedInsight.renewalPeriod && (
                    <RenewalPeriodLabel>{formattedInsight.renewalPeriod}</RenewalPeriodLabel>
                  )}
                </FindingsHeader>
                <ChainDescription>
                  {usingRenewal
                    ? `All documents of type "${typeof displayFile?.fileType === 'string' ? displayFile.fileType : displayFile?.fileType?.name || 'this type'}" in this Building Passport, newest first. Click any document to view its details.`
                    : 'All versions of this document, including superseded ones, newest first. Click any to view its details.'}
                </ChainDescription>
                <ChainTimeline>
                  {historyItems.map((item, i) => (
                    <ChainItem
                      key={item.fileId}
                      $isCurrent={item.isCurrent}
                      $isThis={item.fileId === activeFile?.id}
                      $clickable={item.fileId !== activeFile?.id}
                      onClick={() => handleChainNavigate(item.fileId, item.displayName)}
                    >
                      <ChainDot $isCurrent={item.isCurrent} />
                      <ChainContent>
                        <ChainName $isCurrent={item.isCurrent}>
                          {item.displayName}
                          {item.isCurrent && <CurrentTag>Current, shown on calendar</CurrentTag>}
                          {!item.isCurrent && <CurrentTag $muted>Superseded</CurrentTag>}
                          {item.fileId === activeFile?.id && <CurrentTag $muted>Viewing</CurrentTag>}
                        </ChainName>
                        <ChainDate>
                          {item.dateOfIssue ? `Issued: ${item.dateOfIssue}` : `Uploaded: ${item.createdAt}`}
                          {item.expirationDate && ` · Expires: ${item.expirationDate}`}
                        </ChainDate>
                        {!item.isCurrent && replacedBecause(item.supersessionReason) && (
                          <ChainReason>{replacedBecause(item.supersessionReason)}</ChainReason>
                        )}
                      </ChainContent>
                    </ChainItem>
                  ))}
                </ChainTimeline>
                {bp?.canEdit && historyItems.some(item => item.fileId === activeFile?.id) && (
                  <ChainActions>
                    <OutlineBtn type='button' disabled={versionBusy} onClick={() => changeVersionHistory(true)}>
                      This document is not a version of these
                    </OutlineBtn>
                    <GroupHint>Takes this document out of the history so it stays current on its own. You can put it back.</GroupHint>
                  </ChainActions>
                )}
              </RenewalChainSection>
              )
            })()}

            {(() => {
              // Only show flags that need user attention:
              // - Not auto-corrected (needs manual review)
              // - Auto-corrected but low confidence (user should verify)
              const actionableFlags = (formattedInsight.chainFlags || []).filter(f =>
                !f.autoCorrected || f.confidence < 80
              )
              if (actionableFlags.length === 0) return null
              return (
              <FlagsSection>
                <FindingsHeader>
                  <WarningAmberIcon fontSize='small' style={{ color: '#d97706' }} />
                  Attention Required ({actionableFlags.length})
                </FindingsHeader>
                {actionableFlags.some(f => f.confidence < 60) && (
                  <DisclaimerBanner>
                    Some details below have low confidence and need manual verification.
                  </DisclaimerBanner>
                )}
                {actionableFlags.map((flag, i) => {
                  const isOtherFile = flag.fileId && flag.fileId !== activeFile?.id
                  return (
                    <FlagItem
                      key={flag.id || i}
                      $clickable={isOtherFile}
                      onClick={isOtherFile ? () => handleChainNavigate(flag.fileId, flag.fileDisplayName) : undefined}
                    >
                      <FlagBadge $confidence={flag.confidence}>{flag.confidence}%</FlagBadge>
                      <FlagContent>
                        <FlagDescription>{flag.description}</FlagDescription>
                        {flag.fileDisplayName && isOtherFile && (
                          <FlagFileRef>On: {flag.fileDisplayName} →</FlagFileRef>
                        )}
                        {flag.aiReasoning && flag.aiReasoning !== flag.description && (
                          <FlagReasoning>{flag.aiReasoning}</FlagReasoning>
                        )}
                        {!flag.autoCorrected && (
                          <FlagNote>Requires manual review</FlagNote>
                        )}
                      </FlagContent>
                    </FlagItem>
                  )
                })}
              </FlagsSection>
              )
            })()}
          </>
        ) : (
          <NoInsightContainer>
            {insightError || 'No insight available for this file.'}
          </NoInsightContainer>
        )}
      </Container>
    </Modal>
    {previewFile && /\.pdf/i.test(previewFile.url || previewFile.displayName) && (
      <PdfFullScreenViewer
        open={previewOpen}
        onClose={() => { setPreviewOpen(false); setPreviewFile(null) }}
        file={previewFile}
      />
    )}
    {previewFile && /\.(jpg|jpeg|png|gif|svg|webp)$/i.test(previewFile.url || previewFile.displayName) && (
      <ImageViewer
        open={previewOpen}
        onClose={() => { setPreviewOpen(false); setPreviewFile(null) }}
        file={previewFile}
      />
    )}
    <BulkConvertDialog
      ids={bulkConvertIds}
      open={!!bulkConvertIds}
      approving
      onClose={() => setBulkConvertIds(null)}
      onConverted={() => {
        (bulkConvertIds || []).forEach(id => markConverted(id))
        setBulkConvertIds(null)
        setLinksRefresh(n => n + 1)
        refreshInsight()
        onUpdate?.()
      }}
    />
    <ConvertToTaskDialog
      action={convertTarget}
      open={!!convertTarget}
      onClose={() => setConvertTarget(null)}
      onConverted={a => { markConverted(a.id, (a.linkedTasks || [])[0]); setLinksRefresh(n => n + 1); onUpdate?.() }}
      zIndex={100050}
    />
    <LinkTaskDialog
      open={!!linkTarget}
      bpId={remedialsBpId}
      remedialTitle={linkTarget?.title}
      zIndex={100050}
      onClose={() => setLinkTarget(null)}
      onPick={(task, relation) => {
        const rem = linkTarget
        setLinkTarget(null)
        if (rem) linkExistingTask(rem, task, relation)
      }}
    />
    </>
  )
}

export default GuruInsightModal

const Container = styled.div`
  display: flex;
  flex-direction: column;
  padding: 24px;
  min-height: 300px;
  background-color: #ffffff;
`

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: 300px;
`

const HeaderSection = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 24px;
  margin-bottom: 16px;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 16px;
  }
`

const ThumbnailContainer = styled.div`
  flex-shrink: 0;
`

const Thumbnail = styled.img`
  width: 150px;
  height: 150px;
  object-fit: cover;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transition: transform 0.3s ease;

  &:hover {
    transform: scale(1.05);
  }
`

const NoThumbnail = styled.div`
  width: 150px;
  height: 150px;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: #f0f4f8;
  border-radius: 8px;
  color: #5c6b7a;
  font-size: 14px;
  text-align: center;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
`

const FileInfoContainer = styled.div`
  flex-grow: 1;
  min-width: 0;
  width: 100%;
  display: flex;
  flex-direction: column;
`

const FileName = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const InsightContainer = styled.div`
  margin-top: 16px;
`

const MetadataGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-bottom: 20px;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`

const MetadataCard = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 14px;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
`

const MetadataIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: ${({ $color }) => $color + '14'};
  color: ${({ $color }) => $color};
  flex-shrink: 0;
`

const MetadataContent = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`

const MetadataLabel = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

const MetadataValue = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: #111827;
  word-break: break-word;
`

const FindingsSection = styled.div`
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 16px;
  margin-bottom: 12px;
`

const FindingsHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 15px;
  font-weight: 600;
  color: #111827;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid #f3f4f6;
`

const PriorityTag = styled.span`
  display: inline-block;
  font-size: 10px;
  font-weight: 700;
  color: white;
  background: ${({ $color }) => $color};
  border-radius: 4px;
  padding: 2px 6px;
  margin-right: 6px;
  vertical-align: middle;
`

const RemedialRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px 12px;
  margin-bottom: 8px;
  padding: 8px 10px;
  background: ${({ $dismissed }) => ($dismissed ? '#f4f4f6' : '#f9fafb')};
  border: 1px solid #f0f0f2;
  border-radius: 6px;
  opacity: ${({ $dismissed, $muted }) => ($dismissed ? 0.7 : $muted ? 0.85 : 1)};
  &:last-child { margin-bottom: 0; }
`

const RemedialInfo = styled.div`
  display: flex;
  align-items: center;
  flex: 1 1 240px;
  min-width: 0;
  color: #374151;
  font-size: 14px;
  line-height: 1.45;
`

const ChainReason = styled.span`
  font-size: 12px;
  color: #667085;
  margin-top: 2px;
`

const ChainActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 12px;
  margin-top: 12px;
`

const RemedialText = styled.span`
  min-width: 0;
  text-decoration: ${({ $dismissed }) => ($dismissed ? 'line-through' : 'none')};
  color: ${({ $dismissed }) => ($dismissed ? '#9aa0ab' : 'inherit')};
`







const StageTag = styled.span`
  flex-shrink: 0;
  font-size: 12px;
  font-weight: 700;
  padding: 4px 10px;
  border-radius: 999px;
  white-space: nowrap;
  color: ${({ $stage }) => ($stage === 'resolved' ? '#15803d' : '#17727b')};
  background: ${({ $stage }) => ($stage === 'resolved' ? '#dcfce7' : '#e6f7f9')};
`

const ViewTaskLink = styled(Link)`
  flex-shrink: 0;
  font-size: 12px;
  font-weight: 700;
  color: #17727b;
  text-decoration: underline;
  text-underline-offset: 3px;
  white-space: nowrap;
`

const DismissedTag = styled.span`
  display: inline-flex;
  align-items: center;
  font-size: 11px;
  font-weight: 700;
  color: #9aa0ab;
  white-space: nowrap;
`

const RemedialActions = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 8px;
  flex-shrink: 0;
  margin-left: auto;
`


const BulletPoint = styled.div`
  display: flex;
  align-items: flex-start;
  margin-bottom: 8px;
  padding: 8px 10px;
  background: #f9fafb;
  border-radius: 6px;
  color: #374151;
  font-size: 14px;
  line-height: 1.5;

  &:last-child {
    margin-bottom: 0;
  }

  &::before {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #9ca3af;
    flex-shrink: 0;
    margin-top: 7px;
    margin-right: 10px;
  }
`

const FileActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 8px;
`

const BpNameChip = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  color: #374151;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
`

const PreviewButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.primary};
  background: transparent;
  border: 1px solid ${({ theme }) => theme.colors.primary};
  cursor: pointer;
  transition: all 0.15s;

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.primary};
    color: white;
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`

const FileDetailsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 12px;
  background-color: #f9fafb;
  border-radius: 8px;
  padding: 12px;
`

const FileTagsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 12px;
  padding: 12px;
  background: #f9fafb;
  border-radius: 8px;
`

const TagGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`

const TagGroupLabel = styled.span`
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: #9ca3af;
`

const TagChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`

const TagChip = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: #0f766e;
  background: #ccfbf1;
  border: 1px solid #99f6e4;
  border-radius: 999px;
  padding: 2px 10px;
`

const FileDetailItem = styled.div`
  display: flex;
  align-items: flex-start;
  margin-bottom: 4px;
`

const DetailLabel = styled.span`
  font-weight: 600;
  color: #4b5563;
  width: 140px;
  flex-shrink: 0;
  margin-right: 8px;
  font-size: 14px;

  @media (max-width: 768px) {
    width: 116px;
  }
`

const DetailValue = styled.span`
  color: #1f2937;
  flex: 1;
  font-size: 14px;
  word-break: break-word;
`

const NoInsightContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  color: #6b7280;
  font-size: 16px;
  background-color: #f9fafb;
  border-radius: 8px;
  margin-top: 16px;
`

// Skeleton loading styled components
const LoadingContent = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 16px 0;
`

const LoadingHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 24px;
  margin-bottom: 16px;
`

const SkeletonThumbnail = styled.div`
  width: 150px;
  height: 150px;
  background-color: #e5e7eb;
  border-radius: 8px;
  animation: pulse 1.5s ease-in-out infinite;

  @keyframes pulse {
    0% {
      opacity: 0.6;
    }
    50% {
      opacity: 1;
    }
    100% {
      opacity: 0.6;
    }
  }
`

const LoadingInfo = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
`

const SkeletonDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 16px;
  background-color: #f9fafb;
  border-radius: 8px;
  padding: 12px;
`

const EditButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  cursor: pointer;
  color: #9ca3af;
  padding: 2px;
  margin-left: 4px;
  border-radius: 3px;
  vertical-align: middle;
  transition: color 0.15s;

  &:hover {
    color: #6366f1;
    background: #f3f4f6;
  }
`

const InlineEditRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 2px;
`

const InlineDateInput = styled.input`
  font-size: 13px;
  padding: 3px 6px;
  border: 1px solid #d1d5db;
  border-radius: 5px;
  outline: none;
  font-family: inherit;
  color: #111827;
  width: 140px;

  &:focus {
    border-color: #6366f1;
    box-shadow: 0 0 0 2px rgba(99, 102, 241, 0.15);
  }
`

const InlineEditBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  cursor: pointer;
  padding: 3px;
  border-radius: 4px;
  background: ${({ $cancel }) => $cancel ? '#fee2e2' : '#dcfce7'};
  color: ${({ $cancel }) => $cancel ? '#dc2626' : '#16a34a'};
  transition: opacity 0.15s;

  &:hover { opacity: 0.8; }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`

const SkeletonDetailItem = styled.div`
  height: 16px;
  background-color: #e5e7eb;
  border-radius: 4px;
  animation: pulse 1.5s ease-in-out infinite;
  width: ${props => props.width || '100%'};

  &:nth-child(odd) {
    width: 85%;
  }

  &:nth-child(even) {
    width: 70%;
  }
`

const LoadingMessage = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background-color: #f0f9ff;
  border-radius: 8px;
  color: #0369a1;
  font-weight: 500;
  margin: 16px 0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
`

const SkeletonInsight = styled.div`
  background-color: #f9fafb;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  margin-top: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const DateSourceTag = styled.span`
  display: inline-flex;
  font-size: 9px;
  font-weight: 600;
  padding: 1px 5px;
  border-radius: 3px;
  margin-left: 6px;
  vertical-align: middle;
  color: ${({ $source }) =>
    $source === 'ai_extracted' ? '#0891b2' :
    $source === 'calculated' ? '#7c3aed' :
    $source === 'user' ? '#059669' : '#9ca3af'};
  background: ${({ $source }) =>
    $source === 'ai_extracted' ? '#cffafe' :
    $source === 'calculated' ? '#f5f3ff' :
    $source === 'user' ? '#ecfdf5' : '#f3f4f6'};
`

const ScopeInfo = styled.div`
  font-size: 12px;
  color: #6b7280;
  background: #f9fafb;
  padding: 8px 12px;
  border-radius: 6px;
  margin-bottom: 12px;
  line-height: 1.4;
`

const SupersededBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  font-size: 13px;
  color: #dc2626;
  margin-top: 12px;
`



const ActionsLink = styled(Link)`
  font-size: 13px;
  font-weight: 500;
  color: #D24724;
  text-decoration: none;

  &:hover { text-decoration: underline; }
`

const RemedialGroup = styled.section`
  margin-top: 12px;
  &:first-of-type { margin-top: 4px; }
`

const GroupHead = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 4px 10px;
  margin-bottom: 8px;
`

const GroupTitle = styled.h4`
  margin: 0;
  font-size: 14px;
  font-weight: 700;
  color: #101828;
`

const GroupHint = styled.p`
  margin: 0;
  flex: 1 1 220px;
  font-size: 13px;
  color: #667085;
`

const GroupActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  width: 100%;
  margin-top: 4px;
`

const SolidBtn = styled.button`
  font: inherit;
  font-size: 13px;
  font-weight: 700;
  min-height: 40px;
  padding: 0 14px;
  border: none;
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.secondary};
  color: #fff;
  cursor: pointer;
  white-space: nowrap;
  &:hover:not(:disabled) { filter: brightness(1.06); }
  &:disabled { opacity: 0.55; cursor: not-allowed; }
  &:focus-visible { outline: 2px solid #0c1229; outline-offset: 2px; }
`

const OutlineBtn = styled.button`
  font: inherit;
  font-size: 13px;
  font-weight: 700;
  min-height: 40px;
  padding: 0 14px;
  border: 1px solid ${({ theme }) => theme.colors.secondary};
  border-radius: 8px;
  background: #fff;
  color: #17727b;
  cursor: pointer;
  white-space: nowrap;
  &:hover:not(:disabled) { background: #e6f7f9; }
  &:disabled { opacity: 0.55; cursor: not-allowed; }
  &:focus-visible { outline: 2px solid #0c1229; outline-offset: 2px; }
`

const TextBtn = styled.button`
  font: inherit;
  font-size: 13px;
  font-weight: 600;
  min-height: 40px;
  padding: 0 8px;
  border: none;
  background: none;
  color: #475467;
  text-decoration: underline;
  text-underline-offset: 3px;
  cursor: pointer;
  white-space: nowrap;
  &:hover:not(:disabled) { color: #101828; }
  &:disabled { opacity: 0.55; cursor: not-allowed; }
  &:focus-visible { outline: 2px solid #33becc; outline-offset: 2px; }
`

const DoneToggle = styled.button`
  font: inherit;
  font-size: 13px;
  font-weight: 600;
  min-height: 40px;
  padding: 0;
  border: none;
  background: none;
  color: #475467;
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 3px;
  margin-bottom: 6px;
  &:hover { color: #101828; }
  &:focus-visible { outline: 2px solid #33becc; outline-offset: 2px; }
`

const RemedialsSegue = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px 16px;
  margin-top: 12px;
  padding: 14px 16px;
  background: #f0fbfd;
  border: 1px solid #cdeef1;
  border-radius: 10px;
`

const SegueText = styled.p`
  margin: 0;
  flex: 1 1 260px;
  font-size: 14px;
  line-height: 1.5;
  color: #344054;
`

const SegueButtons = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
`

const SegueLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  min-height: 40px;
  padding: 0 14px;
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.secondary};
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  text-decoration: none;
  &:hover { filter: brightness(1.06); }
  &:focus-visible { outline: 2px solid #0c1229; outline-offset: 2px; }
`




const ToastLine = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`

const ToastUndo = styled.button`
  font: inherit;
  font-weight: 700;
  color: #1d7f8a;
  background: #fff;
  border: 1px solid #33becc;
  border-radius: 6px;
  min-height: 34px;
  padding: 0 12px;
  cursor: pointer;
`

const SegueHint = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 1.5;
  color: #475467;
`

const ReExtractButton = styled.button`
  min-height: 40px;
  font-size: 14px;
  font-weight: 600;
  color: #111827;
  background: #ffffff;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  padding: 6px 12px;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;

  &:hover {
    background: #f3f4f6;
    border-color: #9ca3af;
  }
`


const ContractorsSection = styled.div`
  margin-top: 12px;
  padding: 12px 14px;
  background: #f0fbfd;
  border: 1px solid #cdeef1;
  border-radius: 8px;
`

const ContractorsList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 8px 0 10px;
`

const ContractorChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 10px;
  border-radius: 999px;
  background: #fff;
  border: 1px solid ${({ $inactive }) => ($inactive ? '#e5e7eb' : '#bfe6ec')};
  font-size: 12.5px;
  color: ${({ $inactive }) => ($inactive ? '#9ca3af' : '#0e7490')};
  strong { font-weight: 700; color: ${({ $inactive }) => ($inactive ? '#6b7280' : '#0b5563')}; }
`

const ContractorMuted = styled.span`
  font-size: 9px; font-weight: 800; letter-spacing: 0.05em; text-transform: uppercase;
  color: #9ca3af; background: #f3f4f6; border-radius: 999px; padding: 1px 6px;
`

const ReplacesBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  background: #ecfdf5;
  border: 1px solid #a7f3d0;
  border-radius: 8px;
  font-size: 13px;
  color: #059669;
  margin-top: 8px;
`

const DocStatusChip = styled.span`
  display: inline-flex;
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 4px;
  white-space: nowrap;
  color: ${({ $type }) => $type === 'superseded' ? '#c2410c' : '#059669'};
  background: ${({ $type }) => $type === 'superseded' ? '#fff7ed' : '#ecfdf5'};
  border: 1px solid ${({ $type }) => $type === 'superseded' ? '#fed7aa' : '#a7f3d0'};
`

const CalendarNote = styled.div`
  font-size: 12px;
  color: #6b7280;
  background: #f9fafb;
  border: 1px dashed #d1d5db;
  padding: 8px 12px;
  border-radius: 6px;
  margin-bottom: 12px;
`

const RenewalChainSection = styled.div`
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 16px;
  margin-top: 12px;
`

const RenewalPeriodLabel = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: #7c3aed;
  background: #f5f3ff;
  padding: 2px 8px;
  border-radius: 4px;
  margin-left: auto;
`

const ChainDescription = styled.p`
  font-size: 12px;
  color: #6b7280;
  margin: 0 0 12px 0;
  line-height: 1.4;
`

const ChainTimeline = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  position: relative;
  padding-left: 12px;

  &::before {
    content: '';
    position: absolute;
    left: 5px;
    top: 8px;
    bottom: 8px;
    width: 2px;
    background: #e5e7eb;
  }
`

const ChainItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 8px 8px;
  position: relative;
  border-radius: 6px;
  opacity: ${({ $isCurrent }) => $isCurrent ? 1 : 0.7};
  cursor: ${({ $clickable }) => $clickable ? 'pointer' : 'default'};
  background: ${({ $isThis }) => $isThis ? '#f5f3ff' : 'transparent'};
  transition: background 0.15s;

  &:hover {
    background: ${({ $clickable, $isThis }) => $clickable ? '#faf5ff' : ($isThis ? '#f5f3ff' : 'transparent')};
    opacity: ${({ $clickable, $isCurrent }) => $clickable ? 1 : ($isCurrent ? 1 : 0.7)};
  }
`

const ChainDot = styled.div`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${({ $isCurrent }) => $isCurrent ? '#7c3aed' : '#d1d5db'};
  border: 2px solid ${({ $isCurrent }) => $isCurrent ? '#7c3aed' : '#e5e7eb'};
  flex-shrink: 0;
  margin-top: 4px;
  position: relative;
  z-index: 1;
`

const ChainContent = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`

const ChainName = styled.span`
  font-size: 13px;
  font-weight: ${({ $isCurrent }) => $isCurrent ? 600 : 400};
  color: #111827;
  display: flex;
  align-items: center;
  gap: 6px;
`

const CurrentTag = styled.span`
  font-size: 10px;
  font-weight: 600;
  color: ${({ $muted }) => $muted ? '#6b7280' : '#059669'};
  background: ${({ $muted }) => $muted ? '#f3f4f6' : '#ecfdf5'};
  padding: 1px 6px;
  border-radius: 4px;
`

const ChainDate = styled.span`
  font-size: 11px;
  color: #9ca3af;
  margin-top: 2px;
`

const FlagsSection = styled.div`
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 10px;
  padding: 16px;
  margin-top: 12px;
`

const DisclaimerBanner = styled.div`
  font-size: 12px;
  color: #92400e;
  background: #fef3c7;
  padding: 8px 12px;
  border-radius: 6px;
  margin-bottom: 12px;
`

const FlagItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 8px;
  border-bottom: 1px solid #fde68a;
  border-radius: 4px;
  cursor: ${({ $clickable }) => $clickable ? 'pointer' : 'default'};
  transition: background 0.15s;

  &:last-child { border-bottom: none; }

  &:hover {
    background: ${({ $clickable }) => $clickable ? '#fef3c7' : 'transparent'};
  }
`

const FlagFileRef = styled.div`
  font-size: 11px;
  color: #7c3aed;
  font-weight: 600;
  margin-top: 4px;
`

const BackToOriginalLink = styled.button`
  background: none;
  border: none;
  color: #7c3aed;
  font-size: 12px;
  padding: 0;
  margin-bottom: 8px;
  cursor: pointer;
  text-align: left;

  &:hover {
    text-decoration: underline;
  }
`

const FlagBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 36px;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 700;
  flex-shrink: 0;
  color: white;
  background: ${({ $confidence }) =>
    $confidence >= 80 ? '#059669' :
    $confidence >= 60 ? '#d97706' : '#dc2626'};
`

const FlagContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`

const FlagDescription = styled.div`
  font-size: 13px;
  color: #374151;
`

const FlagReasoning = styled.div`
  font-size: 12px;
  color: #6b7280;
  font-style: italic;
`

const FlagNote = styled.div`
  font-size: 11px;
  color: #dc2626;
  font-weight: 500;
`
